import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as learningSpacesApi from '@/features/learning-spaces/api';
import type {
  CreateLearningSpaceInput,
  LearningSpaceView,
  UpdateLearningSpaceInput,
} from '@/features/learning-spaces/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';
import type { PagedData } from '@/lib/api/types';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/**
 * Tenant-scoped query keys embed the Active Organization id (ADR-026
 * Addendum, AO-9) - Org A's cached learning spaces cannot structurally
 * satisfy an Org B query.
 */
const learningSpacesListKey = (organizationId: string | null, page: number, limit: number) =>
  [TENANT_QUERY_PREFIX, organizationId, 'learning-spaces', 'list', page, limit] as const;
const learningSpaceDetailKey = (organizationId: string | null, id: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'learning-spaces', 'detail', id] as const;

export function useLearningSpaces(
  page = 1,
  limit = 20,
): UseQueryResult<PagedData<LearningSpaceView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: learningSpacesListKey(activeOrganizationId, page, limit),
    queryFn: () => learningSpacesApi.listLearningSpaces(page, limit),
    enabled: activeOrganizationId !== null,
  });
}

export function useLearningSpace(id: string): UseQueryResult<LearningSpaceView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: learningSpaceDetailKey(activeOrganizationId, id),
    queryFn: () => learningSpacesApi.getLearningSpace(id),
    enabled: activeOrganizationId !== null,
  });
}

export function useCreateLearningSpace(): UseMutationResult<
  LearningSpaceView,
  ApiError,
  CreateLearningSpaceInput
> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: CreateLearningSpaceInput) => learningSpacesApi.createLearningSpace(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'learning-spaces', 'list'],
      });
    },
  });
}

export function useUpdateLearningSpace(
  id: string,
): UseMutationResult<LearningSpaceView, ApiError, UpdateLearningSpaceInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: UpdateLearningSpaceInput) =>
      learningSpacesApi.updateLearningSpace(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: learningSpaceDetailKey(activeOrganizationId, id),
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'learning-spaces', 'list'],
      });
    },
  });
}

export function useDeactivateLearningSpace(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (id: string) => learningSpacesApi.deactivateLearningSpace(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'learning-spaces'],
      });
    },
  });
}
